import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard, GlassButton } from '../ui/GlassComponents';
import { FiShield, FiCheck, FiX, FiEye, FiEyeOff, FiExternalLink } from 'react-icons/fi';
import { useConfirm } from '../ui/ConfirmDialog';

const SSOConfiguration = () => {
  const confirm = useConfirm();
  const [ssoConfig, setSsoConfig] = useState({
    enabled: false,
    provider: null,
    tenantId: '',
    clientId: '',
    clientSecret: '',
    hasClientSecret: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchSSOConfig();
  }, []);

  const fetchSSOConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/teams/sso/config', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSsoConfig(response.data.ssoConfig);
      }
    } catch (error) {
      console.error('Error fetching SSO config:', error);
      toast.error('Failed to load SSO configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSsoConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const testConfiguration = async () => {
    if (!ssoConfig.tenantId || !ssoConfig.clientId || !ssoConfig.clientSecret) {
      toast.error('Please fill in all required fields before testing');
      return;
    }

    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/sso/test-config', {
        clientId: ssoConfig.clientId,
        clientSecret: ssoConfig.clientSecret,
        tenantId: ssoConfig.tenantId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('SSO configuration is valid!');
      }
    } catch (error) {
      console.error('Error testing SSO config:', error);
      toast.error(error.response?.data?.message || 'SSO configuration test failed');
    } finally {
      setTesting(false);
    }
  };

  const saveConfiguration = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        provider: 'office365',
        tenantId: ssoConfig.tenantId,
        clientId: ssoConfig.clientId,
        enabled: ssoConfig.enabled
      };

      // Only include client secret if it's been changed
      if (ssoConfig.clientSecret && ssoConfig.clientSecret.trim()) {
        updateData.clientSecret = ssoConfig.clientSecret;
      }

      const response = await axios.put('/api/teams/sso/config', updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSsoConfig(response.data.ssoConfig);
        setIsEditing(false);
        toast.success('SSO configuration saved successfully!');
      }
    } catch (error) {
      console.error('Error saving SSO config:', error);
      toast.error(error.response?.data?.message || 'Failed to save SSO configuration');
    } finally {
      setSaving(false);
    }
  };

  const disableSSO = async () => {
    const ok = await confirm({
      title: 'Disable SSO?',
      description: 'This will remove all SSO configuration. Users will need to sign in with email + password until SSO is set up again.',
      confirmLabel: 'Disable SSO',
      destructive: true
    });
    if (!ok) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete('/api/teams/sso/config', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSsoConfig({
          enabled: false,
          provider: null,
          tenantId: '',
          clientId: '',
          clientSecret: '',
          hasClientSecret: false
        });
        setIsEditing(false);
        toast.success('SSO has been disabled');
      }
    } catch (error) {
      console.error('Error disabling SSO:', error);
      toast.error('Failed to disable SSO');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <GlassCard className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-6">
      {/* SSO Status Card */}
      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FiShield className="h-6 w-6 text-blue-500 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Single Sign-On (SSO)
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enable Office365 authentication for your team
              </p>
            </div>
          </div>
          <div className="flex items-center">
            {ssoConfig.enabled ? (
              <div className="flex items-center text-green-600 dark:text-green-400">
                <FiCheck className="h-5 w-5 mr-2" />
                <span className="font-medium">Enabled</span>
              </div>
            ) : (
              <div className="flex items-center text-gray-500 dark:text-gray-400">
                <FiX className="h-5 w-5 mr-2" />
                <span className="font-medium">Disabled</span>
              </div>
            )}
          </div>
        </div>

        {ssoConfig.enabled && !isEditing && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <FiCheck className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  SSO is active for your team
                </p>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Team members can sign in with their Office365 accounts
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex space-x-3">
          {!isEditing ? (
            <>
              <GlassButton
                onClick={() => setIsEditing(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                {ssoConfig.enabled ? 'Edit Configuration' : 'Configure SSO'}
              </GlassButton>
              {ssoConfig.enabled && (
                <GlassButton
                  onClick={disableSSO}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  disabled={saving}
                >
                  Disable SSO
                </GlassButton>
              )}
            </>
          ) : (
            <>
              <GlassButton
                onClick={saveConfiguration}
                disabled={saving || !ssoConfig.tenantId || !ssoConfig.clientId}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </GlassButton>
              <GlassButton
                onClick={testConfiguration}
                disabled={testing || !ssoConfig.tenantId || !ssoConfig.clientId || !ssoConfig.clientSecret}
                className="bg-yellow-500 hover:bg-yellow-600 text-white"
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </GlassButton>
              <GlassButton
                onClick={() => {
                  setIsEditing(false);
                  fetchSSOConfig(); // Reset to original values
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white"
              >
                Cancel
              </GlassButton>
            </>
          )}
        </div>
      </GlassCard>

      {/* Configuration Form */}
      {isEditing && (
        <GlassCard className="p-6">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Office365 Configuration
          </h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tenant ID *
              </label>
              <input
                type="text"
                value={ssoConfig.tenantId}
                onChange={(e) => handleInputChange('tenantId', e.target.value)}
                placeholder="e.g., contoso.onmicrosoft.com or tenant-uuid"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 backdrop-blur-sm text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Client ID (Application ID) *
              </label>
              <input
                type="text"
                value={ssoConfig.clientId}
                onChange={(e) => handleInputChange('clientId', e.target.value)}
                placeholder="e.g., abc123-def456-ghi789..."
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 backdrop-blur-sm text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Client Secret *
              </label>
              <div className="relative">
                <input
                  type={showClientSecret ? 'text' : 'password'}
                  value={ssoConfig.clientSecret}
                  onChange={(e) => handleInputChange('clientSecret', e.target.value)}
                  placeholder={ssoConfig.hasClientSecret ? 'Enter new secret to update' : 'Enter client secret'}
                  className="w-full px-3 py-2 pr-10 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 backdrop-blur-sm text-gray-900 dark:text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowClientSecret(!showClientSecret)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showClientSecret ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                </button>
              </div>
              {ssoConfig.hasClientSecret && !ssoConfig.clientSecret && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Client secret is configured. Leave blank to keep current secret.
                </p>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="enableSSO"
                checked={ssoConfig.enabled}
                onChange={(e) => handleInputChange('enabled', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="enableSSO" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Enable SSO for this team
              </label>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Setup Instructions */}
      <GlassCard className="p-6">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-6 flex items-center">
          <FiExternalLink className="h-5 w-5 mr-2" />
          Azure Setup Guide
        </h4>
        
        <div className="space-y-6">
          {/* Step 1 */}
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
              1
            </div>
            <div>
              <h5 className="font-medium text-gray-900 dark:text-white mb-1">
                Create Enterprise Application
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Go to <strong>Azure Portal</strong> → <strong>Enterprise Applications</strong> → <strong>New application</strong>
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Name: <strong>"LeaveWise SSO"</strong> | Type: <strong>"Linked"</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
              2
            </div>
            <div>
              <h5 className="font-medium text-gray-900 dark:text-white mb-1">
                Configure Redirect URI
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                In <strong>App registrations</strong> → <strong>Authentication</strong> → <strong>Add platform</strong> → <strong>Web</strong>
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <p className="text-sm text-green-800 dark:text-green-200 font-mono">
                  https://www.leavewise.cloud/api/sso/o365/callback
                </p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
              3
            </div>
            <div>
              <h5 className="font-medium text-gray-900 dark:text-white mb-1">
                Set API Permissions
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                <strong>API permissions</strong> → <strong>Microsoft Graph</strong> → Add: openid, profile, email, User.Read
              </p>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  ⚠️ Don't forget to <strong>Grant admin consent</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
              4
            </div>
            <div>
              <h5 className="font-medium text-gray-900 dark:text-white mb-1">
                Create Client Secret
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                <strong>Certificates & secrets</strong> → <strong>New client secret</strong> → Copy the value immediately
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-800 dark:text-red-200">
                  🔒 Secret is only shown once - copy it now!
                </p>
              </div>
            </div>
          </div>

          {/* Step 5 */}
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
              ✓
            </div>
            <div>
              <h5 className="font-medium text-gray-900 dark:text-white mb-1">
                Complete Configuration
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Copy these values to the form above:
              </p>
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Tenant ID:</span>
                  <span className="font-medium text-gray-900 dark:text-white">Directory (tenant) ID</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Client ID:</span>
                  <span className="font-medium text-gray-900 dark:text-white">Application (client) ID</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Client Secret:</span>
                  <span className="font-medium text-gray-900 dark:text-white">Client secret value</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            <FiExternalLink className="h-4 w-4" />
            <span>Need help? Check the detailed setup guide in your knowledge base</span>
          </div>
        </div>
      </GlassCard>
    </div>
  );
};

export default SSOConfiguration;
